import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import InView from 'react-intersection-observer';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Typography,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
} from '@mui/material';
import { PrismicRichText } from '@prismicio/react';

import theme from 'src/style-system/theme';
import Accordion from '@components/Accordian';
import Header from '@components/Header';
import Button from '@components/Button';
import scrollToRef from '@utils/scrollToRef';
import { AppRoutes } from '@utils/appRoutes';
import PlanBanner from '@components/PlanBanner';

import { faqsContent } from './constant';
import { useStyles } from './useStyles';

const categories = [
  { category_name: 'Subscription', category_id: 0 },
  { category_name: 'Delivery', category_id: 1 },
  { category_name: 'Meals', category_id: 2 },
  { category_name: 'Gift Cards', category_id: 3 },
  { category_name: 'Recent Changes', category_id: 4 },
];

interface IFaqs {
  slices: any;
}

const Faqs = ({ slices }: IFaqs) => {
  const [headerIndex, setHeaderIndex] = useState<number>(0);
  const classes = useStyles();
  const router = useRouter();

  //console.log({ slices });

  const myRef = [
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
    useRef<HTMLDivElement>(null),
  ];

  const type: any = categories.find(
    ({ category_id }: any) => category_id === headerIndex
  )?.category_name;

  const onClickHeaderTitle = (index: number) => {
    //@ts-ignore
    myRef[index].current.style.scrollMarginTop = '120px';
    scrollToRef(myRef[index]);
    setHeaderIndex(index);
  };

  const onVisible = (inView: boolean, index: number) => {
    if (inView) {
      setHeaderIndex(index);
    }
  };

  const onClickWriteToUs = () => {
    router.push(AppRoutes.CONTACT_US);
  };

  const onClickChatWithUs = () => {
    //@ts-ignore
    window?.GorgiasChat?.open();
    // window.fcWidget.open();
    // window.tidioChatApi.open();
  };

  return (
    <>
      <Box className={classes.heroSectionContainer}>
        <Header isBackground={false} />
        <Box className={classes.titleWrapper}>
          <Typography
            variant="h1"
            align="center"
            color="whitePepper.main"
            className={classes.tilte}
          >
            Frequently Asked Questions
          </Typography>
        </Box>
      </Box>
      <Box bgcolor="whitePepper.main">
        <Container maxWidth="xl" disableGutters>
          <Box className={classes.headerContainer}>
            <Box className={classes.headerTitleWrapper}>
              {slices?.map((slice: any, index: number) => (
                <Typography
                  key={slice?.id}
                  variant="body1"
                  color="garamMasala.main"
                  mr={2}
                  onClick={() => onClickHeaderTitle(index)}
                  sx={{
                    flexShrink: 0,
                    textDecoration:
                      headerIndex === index
                        ? `underline solid ${theme.palette.turmeric.main} 10px`
                        : '',
                    textUnderlineOffset: '15px',
                    cursor: 'pointer',
                  }}
                >
                  {slice?.primary?.title || ''}
                </Typography>
              ))}
            </Box>
          </Box>

          <div className={classes.container}>
            <div className={classes.innerContainer}>
              {slices?.map((slice: any, index: number) => {
                const { primary, items } = slice || {};
                return (
                  <InView
                    key={slice.id}
                    threshold={0.3}
                    onChange={(inView: boolean) => onVisible(inView, index)}
                  >
                    <div ref={myRef[index]} className={classes.contentWrapper}>
                      <div className={classes.heading}>
                        <Typography variant="h2" color="cinnamon.main">
                          {primary?.title || ''}
                        </Typography>
                      </div>
                      <div>
                        {items.map((item: any, index: number) => {
                          const { question, answer } = item;

                          return (
                            <div className={classes.accordion} key={index}>
                              <Accordion
                                bgcolor={theme.palette.whitePepper.main}
                                bordercolor={theme.palette.cumin.main}
                              >
                                <AccordionSummary
                                  expandIcon={
                                    <ArrowDropDownIcon
                                      fontSize="large"
                                      sx={{ color: 'garamMasala.main' }}
                                    />
                                  }
                                  aria-controls="panel1a-content"
                                >
                                  <Typography
                                    variant="body1"
                                    align="left"
                                    color="cinnamon.main"
                                  >
                                    {question || ''}
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <PrismicRichText
                                    field={answer}
                                    components={{
                                      paragraph: ({ children }) => (
                                        <Typography
                                          variant="body2"
                                          align="justify"
                                          color="cumin.main"
                                        >
                                          {children}
                                        </Typography>
                                      ),
                                      oList: ({ children }) => {
                                        return (
                                          <ol className={classes.unorderedlist}>
                                            <Typography
                                              variant="body2"
                                              align="justify"
                                              color="cumin.main"
                                            >
                                              {children}
                                            </Typography>
                                          </ol>
                                        );
                                      },
                                      list: ({ children }) => {
                                        return (
                                          <ul className={classes.unorderedlist}>
                                            <Typography
                                              variant="body2"
                                              align="justify"
                                              color="cumin.main"
                                            >
                                              {children}
                                            </Typography>
                                          </ul>
                                        );
                                      },

                                      preformatted: ({ children }) => {
                                        return (
                                          <Typography
                                            variant="body2"
                                            align="justify"
                                            color="cumin.main"
                                          >
                                            {children}
                                          </Typography>
                                        );
                                      },
                                    }}
                                  />
                                </AccordionDetails>
                              </Accordion>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </InView>
                );
              })}

              <div className={classes.needHelp}>
                <Typography variant="h2" align="center" color="cinnamon.main">
                  Still need help?
                </Typography>

                <Typography
                  variant="body1"
                  align="center"
                  color="cumin.main"
                  mb={{ xs: '20px', md: '45px' }}
                  mt="15px"
                >
                  Send us a note or grab us on chat!
                </Typography>
                <div className={classes.btnWrapper}>
                  <Button
                    color="cinnamon"
                    textColor="whitePepper.main"
                    variant="contained"
                    borderRadius="24"
                    height="48"
                    width="200px"
                    onClick={onClickChatWithUs}
                  >
                    Chat With Us
                  </Button>
                  <Button
                    color="turmeric"
                    textColor="garamMasala.main"
                    variant="contained"
                    borderRadius="24"
                    height="48"
                    width="200px"
                    onClick={onClickWriteToUs}
                  >
                    Write To Us
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Box>
      <Box className={classes.feelSection}>
        <PlanBanner isTitleOnMobile={false} isSmallPadding />
      </Box>
    </>
  );
};

export default Faqs;
